"use client";

import React from "react";
import { Spinner } from "@heroui/react";

import { findTextMatch, MatchResult } from "@/utils/textMatching";
import { logger } from "@/telemetry";

export interface TXTViewerProps {
  /** TXT document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the TXT file */
  citationText?: string;

  /** Callback when text is found or not found */
  onMatchResult?: (result: MatchResult | null) => void;
}

/**
 * TXT Viewer component
 * Renders plain text files and highlights citation text
 *
 * This is the simplest viewer since TXT is just raw text with no formatting.
 */
export const TXTViewer: React.FC<TXTViewerProps> = ({
  documentData,
  citationText,
  onMatchResult,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [documentText, setDocumentText] = React.useState<string>("");

  // Convert ArrayBuffer to text
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    let isMounted = true;

    const loadText = async () => {
      try {
        setLoading(true);
        setError(null);

        // Decode ArrayBuffer as UTF-8 text
        const decoder = new TextDecoder("utf-8");
        const text = decoder.decode(documentData);

        if (!isMounted) return;

        setDocumentText(text);
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to load TXT", err);
        if (isMounted) {
          setError("Failed to load text file");
          setLoading(false);
        }
      }
    };

    loadText();

    return () => {
      isMounted = false;
    };
  }, [documentData]);

  /**
   * Highlight matched text in the rendered TXT DOM
   * Simpler than DOCX/PDF since we control the entire text rendering
   */
  const highlightTextInDOM = React.useCallback(
    (container: HTMLElement, matchResult: MatchResult) => {
      try {
        const { position, length } = matchResult;

        // Split text into three parts: before, match, after
        const beforeText = documentText.substring(0, position);
        const matchText = documentText.substring(position, position + length);
        const afterText = documentText.substring(position + length);

        // Clear container
        container.innerHTML = "";

        // Create pre element for proper whitespace handling
        const pre = document.createElement("pre");

        pre.className = "whitespace-pre-wrap break-words text-sm text-default-700 font-mono";
        pre.style.margin = "0";

        // Add before text
        if (beforeText) {
          pre.appendChild(document.createTextNode(beforeText));
        }

        // Add highlighted text
        const mark = document.createElement("mark");

        mark.className = "bg-warning/30 dark:bg-warning/40 px-1 rounded";
        // Remove hardcoded backgroundColor to let Tailwind handle dark mode
        mark.style.padding = "2px 4px";
        mark.style.borderRadius = "2px";
        mark.textContent = matchText;
        pre.appendChild(mark);

        // Add after text
        if (afterText) {
          pre.appendChild(document.createTextNode(afterText));
        }

        container.appendChild(pre);

        // Scroll to highlighted text
        setTimeout(() => {
          mark.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 100);
      } catch (err) {
        logger.error("Failed to highlight text in TXT", err);
      }
    },
    [documentText],
  );

  // Find and highlight citation text
  React.useEffect(() => {
    if (!citationText || !documentText || !containerRef.current) return;

    const matchResult = findTextMatch(documentText, citationText);

    if (onMatchResult) {
      onMatchResult(matchResult);
    }

    if (matchResult) {
      highlightTextInDOM(containerRef.current, matchResult);
    }
  }, [citationText, documentText, onMatchResult, highlightTextInDOM]);

  // Render plain text without highlighting (fallback)
  const renderPlainText = () => {
    if (!documentText) return null;

    return (
      <pre className="whitespace-pre-wrap break-words text-sm text-default-700 font-mono m-0">
        {documentText}
      </pre>
    );
  };

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      {/* Always render container so ref is available */}
      <div
        ref={containerRef}
        className="txt-container max-w-4xl mx-auto"
        style={{
          display: loading || error ? "none" : "block",
        }}
      >
        {/* If no citation to highlight, just show plain text */}
        {!citationText && renderPlainText()}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label="Loading text file..." />
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-danger-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default TXTViewer;
